<template>
  <v-menu
    v-model="open"
    location="bottom end"
    :close-on-content-click="false"
    transition="scale-transition"
    offset="8"
  >
    <template #activator="{ props }">
      <v-btn icon variant="text" v-bind="props" class="notif-btn">
        <v-badge
          :content="unreadCount > 99 ? '99+' : unreadCount"
          :model-value="unreadCount > 0"
          color="red"
        >
          <v-icon size="22" color="#374151">mdi-bell-outline</v-icon>
        </v-badge>
      </v-btn>
    </template>

    <div class="notif-panel">

      <!-- Header -->
      <div class="notif-header">
        <span class="notif-title">Notifications</span>
        <span
          v-if="unreadCount > 0"
          class="notif-mark-all"
          @click="markAllRead"
        >Mark all read</span>
      </div>

      <v-divider />

      <!-- Loading -->
      <div v-if="loading" class="notif-loading">
        <v-skeleton-loader
          v-for="n in 3"
          :key="n"
          type="list-item-two-line"
          class="px-2"
        />
      </div>

      <!-- Notification list -->
      <template v-else-if="notifications.length">
        <div
          v-for="(notif, idx) in notifications"
          :key="notif.id"
          class="notif-item"
          :class="{ 'notif-item--unread': !notif.isOpenned }"
          @click="readNotif(notif)"
        >
          <div class="notif-item-top">
            <span class="notif-item-title">{{ notif.title }}</span>
            <span class="notif-item-time">{{ timeAgo(notif.created_at) }}</span>
          </div>
          <div class="notif-item-body">{{ notif.message }}</div>
          <v-divider v-if="idx < notifications.length - 1" class="mt-3" />
        </div>
      </template>

      <!-- Empty state -->
      <div v-else class="notif-empty">
        <v-icon size="36" color="#cbd5e1">mdi-bell-off-outline</v-icon>
        <div class="notif-empty-text">No notifications yet</div>
      </div>

      <!-- Footer -->
      <v-divider />
      <div class="notif-footer" @click="open = false">
        View All
      </div>

    </div>
  </v-menu>
</template>

<script>
export default {
  name: "NotificationDropdown",
  data: () => ({
    open: false,
    loading: false,
    notifications: [],
  }),
  computed: {
    unreadCount() {
      return this.notifications.filter((n) => !n.isOpenned).length;
    },
  },
  created() {
    this.fetchNotifications();
  },
  methods: {
    fetchNotifications() {
      this.loading = true;
      this.axiosCall("/notifications/getMyNotifs", "GET")
        .then((res) => {
          if (res && res.data) {
            this.notifications = Array.isArray(res.data) ? res.data : [];
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    markAllRead() {
      this.axiosCall("/notifications/markAllAsRead", "POST", this.notifications).then(() => {
        this.notifications.forEach((n) => (n.isOpenned = true));
      });
    },
    readNotif(notif) {
      if (!notif.isOpenned) {
        this.axiosCall("/notifications/markOneAsRead", "POST", { id: notif.id }).then(() => {
          notif.isOpenned = true;
        });
      }
    },
    timeAgo(dateStr) {
      if (!dateStr) return "";
      const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      return `${Math.floor(hours / 24)}d ago`;
    },
  },
};
</script>

<style scoped>
.notif-btn {
  margin-right: 4px;
}

/* ── Panel ── */
.notif-panel {
  width: 360px;
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

/* ── Header ── */
.notif-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px 14px;
}

.notif-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.notif-mark-all {
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
  cursor: pointer;
}

.notif-mark-all:hover {
  text-decoration: underline;
}

/* ── Items ── */
.notif-item {
  padding: 14px 18px;
  cursor: pointer;
  transition: background 0.12s;
}

.notif-item:hover {
  background: #f8fafc;
}

.notif-item--unread {
  background: #f0fdf4;
}

.notif-item--unread:hover {
  background: #dcfce7;
}

.notif-item-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 4px;
}

.notif-item-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.notif-item-time {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  flex-shrink: 0;
}

.notif-item-body {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

/* ── Loading ── */
.notif-loading {
  padding: 8px 0;
}

/* ── Empty ── */
.notif-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 0;
  gap: 8px;
}

.notif-empty-text {
  font-size: 13px;
  color: #94a3b8;
}

/* ── Footer ── */
.notif-footer {
  padding: 13px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: background 0.12s;
}

.notif-footer:hover {
  background: #f8fafc;
  color: #374151;
}
</style>
